import React, {useMemo, useState} from 'react';

import {useCharacter} from '../character-context';
import {Link} from '../../../../lib/routes';
import {ACTION_SECTIONS, ActionEntry, ActionSection, buildActionEntries, STANDARD_ACTIONS} from '../model/action-list';
import {ActionTable, ActionTableGroup} from './action-table';
import {FilterChip, FilterChips} from './filter-chips';

import css from './sheet.module.css';

type ActionFilter = 'all' | 'attacks' | ActionSection | 'limited';

const SECTION_TITLES: Record<ActionSection, string> = {
	action: 'Действия',
	bonusAction: 'Бонусные действия',
	reaction: 'Ответные действия',
	other: 'Другое',
};

const FILTERS: {value: ActionFilter; label: string; matches(entry: ActionEntry): boolean}[] = [
	{value: 'all', label: 'Все', matches: () => true},
	{value: 'attacks', label: 'Атаки', matches: (entry) => entry.isAttack},
	...ACTION_SECTIONS.map((section) => ({
		value: section,
		label: SECTION_TITLES[section],
		matches: (entry: ActionEntry) => entry.section === section,
	})),
	{value: 'limited', label: 'Расходуемое', matches: (entry) => entry.isLimited},
];

export const ActionsTab: React.FC = () => {
	const {catalog, character} = useCharacter();
	const [filter, setFilter] = useState<ActionFilter>('all');
	const entries = useMemo(() => buildActionEntries(catalog, character), [catalog, character]);

	const chips: FilterChip<ActionFilter>[] = FILTERS.map(({value, label, matches}) => ({
		value,
		label,
		count: value === 'all' ? undefined : entries.filter(matches).length,
	}));
	const matches = FILTERS.find(({value}) => value === filter)!.matches;
	const sections = ACTION_SECTIONS.map((section) => ({
		section,
		entries: entries.filter((entry) => entry.section === section && matches(entry)),
	})).filter((group) => group.entries.length);

	const groups: ActionTableGroup[] = sections.map(({section, entries: sectionEntries}) => ({
		key: section,
		title: SECTION_TITLES[section],
		intro: section === 'action' && (filter === 'all' || filter === 'action') ? <StandardActions /> : undefined,
		entries: sectionEntries,
	}));

	return (
		<>
			<FilterChips<ActionFilter> label="Вид действий" items={chips} value={filter} onChange={setFilter} />
			{groups.length ? (
				<ActionTable groups={groups} />
			) : (
				<p className={css.muted}>Ничего не подходит под фильтр.</p>
			)}
		</>
	);
};

const StandardActions: React.FC = () => {
	const {character} = useCharacter();
	return (
		<p className={css.standardActions}>
			<span className={css.muted}>Действия в бою: </span>
			{STANDARD_ACTIONS.map(({glossaryId, title}, index) => (
				<React.Fragment key={glossaryId}>
					{index > 0 && ', '}
					<Link route="rule" params={{id: glossaryId}} target="_blank">
						{title}
					</Link>
					{glossaryId === 'attack' && character.attacksPerAction > 1 && ` (×${character.attacksPerAction})`}
				</React.Fragment>
			))}
		</p>
	);
};
